// Detección de muletillas + silencios para autocut, portado de
// premiere-plugin/src/agent/tools.ts (autocut_interview). Lógica PURA: opera
// sobre las palabras del transcript (en coords del MEDIA/source) y devuelve los
// rangos a cortar + los segmentos a conservar. Sin dependencias del editor.

export interface TranscriptWord {
  text: string;
  start: number; // segundos en el MEDIA/source
  end: number;
}

export interface AutocutCut {
  start: number; // media coords
  end: number;
  reason: "filler" | "silence";
  detail: string;
}

export interface KeepSegment {
  start: number; // media coords
  end: number;
  length: number;
}

export interface AutocutResult {
  cuts: AutocutCut[];
  keepSegments: KeepSegment[];
  totalRemoved: number;
  fillersChecked: number;
}

export interface AutocutOptions {
  fillerWords?: string[]; // override; si no, DEFAULT_FILLER_WORDS
  maxSilenceSeconds?: number; // default 2.5, clamp [0.3, 10]
  paddingSeconds?: number; // default 0.3, clamp [0, 0.6]
  removeFillerWords?: boolean; // default true
  removeSilences?: boolean; // default true
  windowStart: number; // inicio de la ventana del clip (media coords) = mediaOffset
  windowEnd: number; // fin = mediaOffset + duration
}

// Lista por defecto (español) — idéntica al plugin.
export const DEFAULT_FILLER_WORDS = [
  "eh",
  "ehh",
  "ehhh",
  "mmm",
  "mm",
  "este",
  "tipo",
  "o sea",
  "digamos",
  "viste",
  "obviamente",
  "básicamente",
  "como que",
  "nada",
  "bueno",
];

const MIN_CUT_DURATION = 0.05;
const MERGE_THRESHOLD = 0.12;
// Keeps más cortos que esto se absorben en el corte adyacente (evita sub-clips
// "flash" de 1-3 frames entre dos cortes pegados). 0.15s < cualquier palabra
// real corta ("sí"/"no" ~0.2-0.4s), así que no borra contenido legítimo.
const MIN_KEEP_SEGMENT = 0.15;
// Pausa mínima (antes o después) para considerar muletilla a una palabra
// ambigua. Una muletilla real ("este...", "...o sea...") tiene una pausa de
// duda; el contenido legítimo ("este video") fluye sin gap.
const ISOLATION_GAP = 0.2;

// Normaliza una palabra para matchear contra la lista de muletillas:
// lowercase, sin acentos, sin puntuación al borde.
export function normalizeWord(w: string): string {
  return (w || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/^[.,;:!?¿¡"'()\s]+/, "")
    .replace(/[.,;:!?¿¡"'()\s]+$/, "")
    .trim();
}

// Muletillas INEQUÍVOCAS (sonidos de duda): se cortan siempre, sin importar el
// contexto. El resto de DEFAULT_FILLER_WORDS (este, tipo, nada, bueno, viste…)
// son palabras que TAMBIÉN son contenido legítimo, así que solo se cortan si
// están aisladas (rodeadas de pausa) o repetidas.
const UNAMBIGUOUS_FILLERS = new Set(
  ["eh", "ehh", "ehhh", "mmm", "mm"].map(normalizeWord),
);

export function detectAutocutRanges(
  words: TranscriptWord[],
  opts: AutocutOptions,
): AutocutResult {
  const fillers = (
    opts.fillerWords && opts.fillerWords.length > 0
      ? opts.fillerWords
      : DEFAULT_FILLER_WORDS
  ).map((s) => normalizeWord(s));
  const maxSilence = Math.max(
    0.3,
    Math.min(10, Number(opts.maxSilenceSeconds) || 2.5),
  );
  const padding = Math.max(
    0,
    Math.min(0.6, Number(opts.paddingSeconds) || 0.3),
  );
  const removeFillers = opts.removeFillerWords !== false;
  const removeSilences = opts.removeSilences !== false;
  const winStart = opts.windowStart;
  const winEnd = opts.windowEnd;

  // 1) Rangos crudos a cortar (media coords).
  const rawCuts: AutocutCut[] = [];

  if (removeFillers) {
    for (let i = 0; i < words.length; i++) {
      const w = words[i];
      const norm = normalizeWord(w.text);
      if (!fillers.includes(norm)) continue;

      const prev = words[i - 1];
      const next = words[i + 1];
      const prevEnd = prev ? prev.end : -Infinity;
      const nextStart = next ? next.start : Infinity;

      // Las muletillas ambiguas (este/tipo/nada/bueno/viste…) también son
      // contenido legítimo: solo cortarlas si están AISLADAS (pausa antes o
      // después) o REPETIDAS. Las inequívocas (eh/mmm…) se cortan siempre.
      if (!UNAMBIGUOUS_FILLERS.has(norm)) {
        const gapBefore = prev ? w.start - prev.end : Infinity;
        const gapAfter = next ? next.start - w.end : Infinity;
        const repeated =
          (prev && normalizeWord(prev.text) === norm) ||
          (next && normalizeWord(next.text) === norm);
        const isolated = gapBefore > ISOLATION_GAP || gapAfter > ISOLATION_GAP;
        if (!isolated && !repeated) continue; // contenido legítimo → no cortar
      }

      // Clampear el padding a los bordes de las palabras vecinas: nunca comerse
      // la palabra real de al lado (causa de "corta la última palabra a la
      // mitad"). Hacia un silencio el padding sí se expande (eso es deseable).
      const s = Math.max(w.start - padding, prevEnd);
      const e = Math.min(w.end + padding, nextStart);
      if (e > s) {
        rawCuts.push({ start: s, end: e, reason: "filler", detail: w.text });
      }
    }
  }

  if (removeSilences) {
    for (let i = 0; i < words.length - 1; i++) {
      const gap = words[i + 1].start - words[i].end;
      if (gap > maxSilence) {
        const s = words[i].end + padding;
        const e = words[i + 1].start - padding;
        if (e > s) {
          rawCuts.push({
            start: s,
            end: e,
            reason: "silence",
            detail: `${gap.toFixed(2)}s gap`,
          });
        }
      }
    }
  }

  // 2) Clamp a [winStart, winEnd], descartar < MIN_CUT_DURATION.
  const clipped = rawCuts
    .map((c) => ({
      ...c,
      start: Math.max(winStart, c.start),
      end: Math.min(winEnd, c.end),
    }))
    .filter((c) => c.end - c.start >= MIN_CUT_DURATION);

  // 3) Sort + merge adyacentes (gap <= MERGE_THRESHOLD).
  clipped.sort((a, b) => a.start - b.start);
  const cuts: AutocutCut[] = [];
  for (const c of clipped) {
    const last = cuts[cuts.length - 1];
    if (last && c.start - last.end <= MERGE_THRESHOLD) {
      last.end = Math.max(last.end, c.end);
      last.detail = `${last.detail} + ${c.detail}`;
    } else {
      cuts.push({ ...c });
    }
  }

  // 4) Keep segments (complemento dentro de [winStart, winEnd]). Los keeps más
  // cortos que MIN_KEEP_SEGMENT se ABSORBEN (no se conservan): así evitamos
  // sub-clips "flash" de 1-3 frames entre dos cortes pegados.
  const keepSegments: KeepSegment[] = [];
  let cursor = winStart;
  for (const cut of cuts) {
    const segLen = cut.start - cursor;
    if (segLen >= MIN_KEEP_SEGMENT) {
      keepSegments.push({ start: cursor, end: cut.start, length: segLen });
    }
    cursor = Math.max(cursor, cut.end);
  }
  const tailLen = winEnd - cursor;
  if (tailLen >= MIN_KEEP_SEGMENT) {
    keepSegments.push({ start: cursor, end: winEnd, length: tailLen });
  }

  // totalRemoved se deriva de lo que REALMENTE se conserva (no de la suma de
  // cuts), para que sea consistente con los sub-clips y el ripple del executor
  // incluso cuando se absorben keeps diminutos.
  const keptTotal = keepSegments.reduce((s, k) => s + k.length, 0);
  const totalRemoved = winEnd - winStart - keptTotal;

  return { cuts, keepSegments, totalRemoved, fillersChecked: fillers.length };
}
